import { afterNextRender, AfterRenderRef, DestroyRef, inject, Injector } from '@angular/core';

/** Schedule only explicit Map selections; data updates never request a scroll. */
export function createMapViewScroller(target: () => HTMLElement | undefined, isMap: () => boolean) {
  const injector = inject(Injector);
  const destroyRef = inject(DestroyRef);
  let pending: AfterRenderRef | undefined;
  const cancel = () => {
    pending?.destroy();
    pending = undefined;
  };
  destroyRef.onDestroy(cancel);

  return {
    cancel,
    request() {
      cancel();
      pending = afterNextRender(
        () => {
          pending = undefined;
          const anchor = target();
          if (destroyRef.destroyed || !isMap() || !anchor?.isConnected) return;

          // Scroll only the owning container, leaving horizontal and ancestor scroll alone.
          let container = anchor.parentElement;
          while (
            container &&
            !(
              container.scrollHeight > container.clientHeight &&
              /auto|scroll/.test(getComputedStyle(container).overflowY)
            )
          ) {
            container = container.parentElement;
          }
          const isDocument =
            !container || container === document.body || container === document.documentElement;
          const viewportTop = isDocument
            ? 0
            : container!.getBoundingClientRect().top + container!.clientTop;
          const headerBottom =
            document.querySelector('header.fixed-top')?.getBoundingClientRect().bottom ?? 0;
          const visibleTop = Math.max(viewportTop, headerBottom);
          const currentTop = isDocument ? window.scrollY : container!.scrollTop;
          const top = Math.max(0, currentTop + anchor.getBoundingClientRect().top - visibleTop);
          const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth';
          if (isDocument) window.scrollTo({ top, behavior });
          else container!.scrollTo({ top, behavior });
        },
        { injector }
      );
    },
  };
}
